// One filter, written and read as one predicate.
//
// The filter chip writes some filters as several SQL predicates joined by
// AND / OR: a JSON filter on a text column declared as JSON
// (`<validity guard> AND <test>`), JSON `!=` (`(x IS NULL OR x <> 'v')`),
// the is-empty checks. Spliced bare into a WHERE / HAVING they became
// separate conditions — `a OR guard AND test` binds the guard to one side
// only, and the Visual view showed two pills where editing one dropped the
// other.
//
// Writing: `parenthesizeCompound` wraps text with a top-level AND / OR (a
// BETWEEN's AND does not count) so it lands as ONE parenthesised predicate.
// Reading: `unwrapOuterParens` hands the chip parser the inside of such a
// group, and `foldWholeGroups` turns an explicit group the caller recognises
// as one filter back into a leaf, so every view shows it as one pill.
//
// Line comments: a predicate may end in `-- note` (MySQL also `# note`), and
// the WHERE planners' fragment / node ranges include such a trailing comment.
// Anything the planners write after it on the same line — the closing
// parenthesis of a wrap, an appended `AND …`, the rest of the clause after an
// edited condition — would be commented out. `parenthesize` closes on the next
// line, and `outsideLineComments` starts / ends written text on a new line
// where a comment would swallow it. Block comments close themselves.

import type { ParseOptions } from '../../sql-grammar/model';
import type { BooleanNode } from '../types';
import { skipContextFor, trySkipNonCode } from '../classify/tokenSkip';
import { skipParen } from '../scope/aliasScope.scanner';
import { scanTopLevelKeywords } from '../structure/scanner';

/** True when `text` joins two or more predicates with a top-level AND / OR. */
export function hasTopLevelConnector(text: string, options: ParseOptions): boolean {
  let between = false;
  for (const k of scanTopLevelKeywords(text, 0, text.length, options)) {
    if (k.upper === 'BETWEEN') between = true;
    else if (k.upper === 'AND' && between) between = false;
    else if (k.upper === 'AND' || k.upper === 'OR') return true;
  }
  return false;
}

/** `text` as one predicate: parenthesised when it has a top-level AND / OR. */
export function parenthesizeCompound(text: string, options: ParseOptions): string {
  return hasTopLevelConnector(text, options) ? parenthesize(text, options) : text;
}

/** `text` in one pair of parentheses — closed on the next line after a trailing line comment. */
export function parenthesize(text: string, options: ParseOptions): string {
  return `(${text}${endsInLineComment(text, options) ? '\n' : ''})`;
}

/**
 * Whether `text` ends inside a line comment — `--` on every engine, `#` on
 * MySQL — so SQL written after it on the same line would be commented out.
 * Comment markers inside strings and quoted identifiers do not count.
 */
export function endsInLineComment(text: string, options: ParseOptions): boolean {
  const ctx = skipContextFor(options);
  let inComment = false;
  for (let i = 0; i < text.length; ) {
    const next = trySkipNonCode(text, i, text.length, ctx);
    if (next > i) {
      const line = text.startsWith('--', i) || (ctx.supportsHashComment && text[i] === '#');
      inComment = line && next === text.length;
      i = next;
      continue;
    }
    if (!/\s/.test(text[i]!)) inComment = false;
    i += 1;
  }
  return inComment;
}

/** The part of an edit context `outsideLineComments` reads. */
type StatementText = {
  readonly source: string;
  readonly options: ParseOptions;
  readonly statementInfo: { readonly range: { readonly startOffset: number } };
};

/**
 * `text` as written over `source[start, end)` of the statement (an insert when
 * `start === end`), kept clear of line comments: it starts on a new line when
 * a line comment runs up to `start`, and ends with a line break when it ends
 * in a line comment and more SQL follows on the same line.
 */
export function outsideLineComments(ctx: StatementText, start: number, end: number, text: string): string {
  let out = text;
  if (endsInLineComment(ctx.source.slice(ctx.statementInfo.range.startOffset, start), ctx.options)) {
    out = `\n${out.replace(/^[ \t]+/, '')}`;
  }
  if (endsInLineComment(out, ctx.options) && !/^[ \t]*(?:\r?\n|$)/.test(ctx.source.slice(end))) out = `${out}\n`;
  return out;
}

/** `text` without the parentheses that wrap all of it (`((a))` → `a`). */
export function unwrapOuterParens(text: string, options: ParseOptions): string {
  const ctx = skipContextFor(options);
  let t = text.trim();
  while (t.startsWith('(') && skipParen(t, 0, t.length, ctx) === t.length && t.endsWith(')')) {
    t = t.slice(1, -1).trim();
  }
  return t;
}

/**
 * The tree with every explicit, non-negated group of two or more members whose
 * source text `isWhole` accepts replaced by a leaf over the same range, so a
 * multi-part filter renders — and is edited / removed by its path — as one
 * condition. Other groups keep their shape; `source` is the text the node
 * ranges index into.
 */
export function foldWholeGroups(
  node: BooleanNode,
  source: string,
  isWhole: (text: string) => boolean,
): BooleanNode {
  if (node.kind === 'leaf') return node;
  const text = source.slice(node.range.startOffset, node.range.endOffset);
  if (node.explicit && !node.negated && node.children.length > 1 && isWhole(text)) {
    return { kind: 'leaf', range: node.range, text, negated: false };
  }
  return { ...node, children: node.children.map((child) => foldWholeGroups(child, source, isWhole)) };
}

/**
 * The leaves every row the clause keeps must satisfy: the tree's AND-joined
 * members, through AND groups (implicit or parenthesised). An OR group or a
 * NOT contributes none — no single member of it holds for every row.
 */
export function conjunctLeaves(node: BooleanNode): readonly Extract<BooleanNode, { kind: 'leaf' }>[] {
  if (node.negated) return [];
  if (node.kind === 'leaf') return [node];
  return node.connector === 'AND' ? node.children.flatMap(conjunctLeaves) : [];
}
